import type {
  CappedWizardAsset,
  WizardAsset,
} from '@/features/dashboard/components/watchlist-wizard/asset-grouping'
import {
  formatExpiryLabel,
  isDefaultOptionQuote,
} from '@/features/dashboard/components/watchlist-wizard/asset-grouping'
import {
  SUPPORTED_EXCHANGE_LABELS,
  SUPPORTED_EXCHANGES as WIZARD_EXCHANGES,
} from '@/features/dashboard/lib/supported-exchanges'
import { watchlistEntryKey } from '@/features/dashboard/lib/watchlist-entry'
import { isPerpInstrument } from '@/features/hedge/lib/instrument-display'
import { safeParseFloat } from '@/lib/utils'
import { Exchange, Instrument, InstrumentKind } from '@/types/generated/common/types'

import type { MarketType } from './at-filter-match'

export interface MarketTypeGroup {
  key: MarketType
  label: string
  instruments: Instrument[]
}

export interface ExchangeGroup {
  exchange: Exchange
  label: string
  instruments: Instrument[]
}

export type WizardGroup = CappedWizardAsset | ExchangeGroup

export type DetailRow =
  | {
      kind: 'section'
      key: string
      label: string
      count: number
      instruments: Instrument[]
      /** Nesting level within the venue pane — 0 (default) for a market-type
       * header, 1 for an options venue/quote group, 2 for an expiry group
       * within it. Purely a rendering hint (indentation); collapse state is
       * still tracked per-key regardless of depth. */
      depth?: number
    }
  | {
      kind: 'instrument'
      key: string
      instrument: Instrument
      indentation?: 'type' | 'expiry' | 'detail'
      /** Overrides the row's displayed title (normally `instrument.symbol`) —
       * used for a collapsed options representative row, e.g. "BTC_USDC"
       * standing in for a specific nearest-expiry/nearest-strike contract. */
      label?: string
      /** Overrides the row's subtitle (normally the enrichment display name)
       * alongside `label` — e.g. "Bitcoin Options" rather than exposing the
       * specific nearest-expiry/nearest-strike contract symbol picked under
       * the hood. */
      sublabel?: string
      /** Show the contract's implied vol ("ATM IV") instead of its price — set on a
       * collapsed option-chain row, whose instrument is the chain's nearest-expiry,
       * nearest-the-money contract, so its IV reads as the chain's ATM IV. */
      showAtmIv?: boolean
    }

export const isAssetGroup = (group: WizardGroup): group is CappedWizardAsset => 'key' in group

// Uses the asset's `.id` (unique group identity), not `.key` (display key) — this key
// must stay unique per group. Single source of truth for this
// format — watchlist-master-row.tsx's `data-keyboard-row-key` calls this directly
// rather than re-deriving the same string.
export const wizardGroupKey = (group: WizardGroup) =>
  isAssetGroup(group) ? `asset:${group.id}` : `exchange:${group.exchange}`

const MARKET_TYPE_ORDER: { key: MarketType; label: string }[] = [
  { key: 'perpetual', label: 'Perp' },
  { key: 'spot', label: 'Spot' },
  { key: 'future', label: 'Futures' },
  { key: 'options', label: 'Options' },
]

export const marketTypeOf = (instrument: Instrument): MarketType | null => {
  if (instrument.kind === InstrumentKind.INSTRUMENT_KIND_SPOT) return 'spot'
  if (instrument.kind === InstrumentKind.INSTRUMENT_KIND_OPTION) return 'options'
  if (instrument.kind === InstrumentKind.INSTRUMENT_KIND_FUTURE) {
    return isPerpInstrument(instrument) ? 'perpetual' : 'future'
  }
  return null
}

export const groupByMarketType = (instruments: Instrument[]): MarketTypeGroup[] => {
  const groups = new Map<MarketType, Instrument[]>()
  for (const instrument of instruments) {
    const type = marketTypeOf(instrument)
    if (!type) continue
    const group = groups.get(type)
    if (group) group.push(instrument)
    else groups.set(type, [instrument])
  }
  return MARKET_TYPE_ORDER.flatMap(({ key, label }) => {
    const group = groups.get(key)
    return group ? [{ key, label, instruments: group }] : []
  })
}

const exchangeLabel = (exchange: Exchange): string => SUPPORTED_EXCHANGE_LABELS.get(exchange) ?? ''

/** Display order for venues everywhere in the wizard: alphabetical by exchange label. */
export const compareExchangesForDisplay = (a: Exchange, b: Exchange): number =>
  exchangeLabel(a).localeCompare(exchangeLabel(b))

export const groupByExchange = (instruments: Instrument[]): ExchangeGroup[] => {
  const groups = new Map<Exchange, Instrument[]>()
  for (const instrument of instruments) {
    if (!instrument.isActive) continue
    const group = groups.get(instrument.exchange)
    if (group) group.push(instrument)
    else groups.set(instrument.exchange, [instrument])
  }
  return WIZARD_EXCHANGES.flatMap(({ exchange, label }) => {
    const group = groups.get(exchange)
    return group ? [{ exchange, label, instruments: group }] : []
  }).sort((a, b) => compareExchangesForDisplay(a.exchange, b.exchange))
}

export interface OptionsVenueQuoteGroup {
  /** `${exchange}:${quoteCurrency}` — unique per venue+settlement-currency combo. */
  key: string
  exchange: Exchange
  /** e.g. "Deribit BTC" for the default (USD) quote, "Deribit BTC_USDC" otherwise. */
  label: string
  /** Just the base(+quote) part of `label`, with the exchange name stripped —
   * e.g. "BTC" or "BTC_USDC". Used where the exchange is already conveyed by
   * an icon/column elsewhere, so it doesn't need to repeat inside the label. */
  baseQuoteLabel: string
  instruments: Instrument[]
}

// An asset's option chain isn't one flat list — the same base can be quoted
// in more than one currency on the same venue (Deribit trades BTC options in
// both USD and USDC), and each venue has its own expiry calendar. Grouping by
// venue+quote first is what lets the venue pane show "Deribit BTC", "Deribit
// BTC_USDC", "Bybit BTC-USDT" as siblings before drilling into either one's
// expiries.
//
// Returned in venue PREFERENCE order (SUPPORTED_EXCHANGES: Deribit first) — callers that
// pick "the" chain rely on that. For on-screen order use sortOptionsVenueGroupsForDisplay.
//
// `assetKey` names the chain after the asset (e.g. "BTC") rather than the contracts'
// own baseCurrency, which on an options-on-futures venue is the underlying future's
// symbol (GLBX: "BTCF7").
export const groupOptionsByVenueQuote = (
  instruments: Instrument[],
  assetKey?: string
): OptionsVenueQuoteGroup[] => {
  const byExchange = new Map<Exchange, Map<string, Instrument[]>>()
  for (const instrument of instruments) {
    let quoteGroups = byExchange.get(instrument.exchange)
    if (!quoteGroups) {
      quoteGroups = new Map()
      byExchange.set(instrument.exchange, quoteGroups)
    }
    const group = quoteGroups.get(instrument.quoteCurrency)
    if (group) group.push(instrument)
    else quoteGroups.set(instrument.quoteCurrency, [instrument])
  }

  return WIZARD_EXCHANGES.flatMap(({ exchange, label: exchangeLabel }) => {
    const quoteGroups = byExchange.get(exchange)
    if (!quoteGroups) return []
    return [...quoteGroups.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([quoteCurrency, groupInstruments]) => {
        const baseCurrency = groupInstruments[0].baseCurrency
        const base = assetKey || baseCurrency
        const baseQuoteLabel = isDefaultOptionQuote(baseCurrency, quoteCurrency)
          ? base
          : `${base}_${quoteCurrency}`
        return {
          key: `${exchange}:${quoteCurrency}`,
          exchange,
          label: `${exchangeLabel} ${baseQuoteLabel}`,
          baseQuoteLabel,
          instruments: groupInstruments,
        }
      })
  })
}

/** Venue groups in on-screen order (alphabetical by exchange; quote order kept within one). */
export const sortOptionsVenueGroupsForDisplay = (
  groups: OptionsVenueQuoteGroup[]
): OptionsVenueQuoteGroup[] =>
  groups.toSorted((a, b) => compareExchangesForDisplay(a.exchange, b.exchange))

/**
 * Whether a chain is options on a dated future (CME/GLBX: each contract's baseCurrency is
 * the underlying future's symbol) rather than on the spot asset, so it can be named
 * "Bitcoin Futures Options" instead of passing for the asset's plain option chain.
 */
export const isFuturesOptionChain = (
  chain: Instrument[],
  assetInstruments: Instrument[]
): boolean => {
  const first = chain[0]
  if (!first) return false
  if (first.exchange === Exchange.EXCHANGE_GLBX) return true
  return assetInstruments.some(
    (instrument) =>
      instrument.exchange === first.exchange &&
      instrument.symbol === first.baseCurrency &&
      marketTypeOf(instrument) === 'future'
  )
}

export const groupOptionsByExpiry = (instruments: Instrument[]) => {
  const groups = new Map<number, Instrument[]>()
  for (const instrument of instruments) {
    const expiryMs = Number(instrument.expirationTsMs ?? 0)
    const group = groups.get(expiryMs)
    if (group) group.push(instrument)
    else groups.set(expiryMs, [instrument])
  }
  return [...groups.entries()]
    .sort(([a], [b]) => a - b)
    .map(([expiryMs, expiryInstruments]) => ({
      expiryMs,
      label: formatExpiryLabel(expiryMs),
      instruments: expiryInstruments,
    }))
}

// Picks the strike closest to `referencePrice` out of a single expiry's
// contracts. With no live price yet (fallback ticker hasn't arrived), falls
// back to the middle strike of the sorted chain — a reasonable near-the-money
// guess until a real price is available.
export const nearestStrikeInstrument = (
  instruments: Instrument[],
  referencePrice: number | undefined
): Instrument | undefined => {
  if (instruments.length === 0) return undefined
  if (referencePrice === undefined || !Number.isFinite(referencePrice)) {
    const byStrike = [...instruments].sort(
      (a, b) => safeParseFloat(a.strikePrice) - safeParseFloat(b.strikePrice)
    )
    return byStrike[Math.floor(byStrike.length / 2)]
  }
  let best: Instrument | undefined
  let bestDiff = Number.POSITIVE_INFINITY
  for (const instrument of instruments) {
    const diff = Math.abs(safeParseFloat(instrument.strikePrice) - referencePrice)
    if (diff < bestDiff) {
      best = instrument
      bestDiff = diff
    }
  }
  return best
}

// The one contract that stands in for an entire venue+quote option chain in
// the watchlist wizard's collapsed multi-select view: the nearest expiry,
// and within it the strike closest to the live underlying price.
//
// "Nearest" skips any expiry that's already passed `now` — the catalog can
// still carry an already-expired listing (not yet purged, or a same-day
// expiry whose settlement time has passed) sorted ahead of the real live
// ones, and picking that one adds a tab that gets pruned by
// pruneExpiredWatchListTabs (use-data-manager-sync.ts) moments later, reading
// as "added then vanished". Falls back to the nearest expiry regardless of
// expiry if every expiry present is already past `now` — surfacing a stale
// contract still beats surfacing nothing.
export const representativeOptionInstrument = (
  instruments: Instrument[],
  referencePrice: number | undefined,
  now: number = Date.now()
): Instrument | undefined => {
  const expiryGroups = groupOptionsByExpiry(instruments)
  const nearestExpiry = expiryGroups.find((group) => group.expiryMs > now) ?? expiryGroups[0]
  if (!nearestExpiry) return undefined
  return nearestStrikeInstrument(nearestExpiry.instruments, referencePrice)
}

// Default collapse state for the venue pane's Options subtree: only the
// very first venue/quote group starts open, and within it only its first
// expiry — every sibling venue group and every other expiry starts
// collapsed, so selecting a major asset doesn't dump its entire option
// chain on screen at once. Perp/Spot/Futures sections are unaffected (they
// were never nested this deep to begin with).
export function computeDefaultCollapsedOptionSections(instruments: Instrument[]): Set<string> {
  const collapsed = new Set<string>()
  const optionsGroup = groupByMarketType(instruments).find((group) => group.key === 'options')
  if (!optionsGroup) return collapsed

  sortOptionsVenueGroupsForDisplay(groupOptionsByVenueQuote(optionsGroup.instruments)).forEach(
    (venueGroup, venueIndex) => {
      const venueKey = `options:${venueGroup.key}`
      if (venueIndex > 0) collapsed.add(venueKey)

      groupOptionsByExpiry(venueGroup.instruments).forEach((expiryGroup, expiryIndex) => {
        if (venueIndex === 0 && expiryIndex === 0) return
        collapsed.add(`${venueKey}:${expiryGroup.expiryMs}`)
      })
    }
  )

  return collapsed
}

// Same ordering the row-render call sites sort by — shared so the keyboard-nav
// flattening below walks rows in the exact order they're rendered in. Venue, then
// nearest expiry first (dated futures; perps/spot carry none and tie), then symbol —
// a symbol sort alone put CME's "BTCF7" (Jan) ahead of "BTCZ6" (Dec) and scattered
// every venue's dated futures out of calendar order.
export const compareInstrumentsForDisplay = (a: Instrument, b: Instrument): number =>
  compareExchangesForDisplay(a.exchange, b.exchange) ||
  Number(a.expirationTsMs ?? 0) - Number(b.expirationTsMs ?? 0) ||
  a.symbol.localeCompare(b.symbol)

export interface SearchNavRow {
  instrument: Instrument
  // Index into the top-level `assets` array — Virtuoso only virtualizes at
  // asset-group granularity, so that's the unit `scrollToIndex` can target.
  assetIndex: number
}

// Flattens the (already-filtered, auto-expanded) asset tree into the exact
// row order it renders in, so arrow-key navigation can walk it as one list.
export const flattenAssetsForNav = (assets: WizardAsset[]): SearchNavRow[] => {
  const rows: SearchNavRow[] = []
  assets.forEach((asset, assetIndex) => {
    for (const typeGroup of groupByMarketType(asset.instruments)) {
      if (typeGroup.key === 'options') {
        for (const expiryGroup of groupOptionsByExpiry(typeGroup.instruments)) {
          for (const instrument of [...expiryGroup.instruments].sort(
            compareInstrumentsForDisplay
          )) {
            rows.push({ instrument, assetIndex })
          }
        }
      } else {
        for (const instrument of [...typeGroup.instruments].sort(compareInstrumentsForDisplay)) {
          rows.push({ instrument, assetIndex })
        }
      }
    }
  })
  return rows
}

export const flattenExchangeGroupsForNav = (groups: ExchangeGroup[]): SearchNavRow[] => {
  const rows: SearchNavRow[] = []
  groups.forEach((group, assetIndex) => {
    for (const typeGroup of groupByMarketType(group.instruments)) {
      for (const instrument of [...typeGroup.instruments].sort(compareInstrumentsForDisplay)) {
        rows.push({ instrument, assetIndex })
      }
    }
  })
  return rows
}

export const flattenGroupsForNav = (groups: WizardGroup[]): SearchNavRow[] => {
  const rows: SearchNavRow[] = []
  groups.forEach((group, assetIndex) => {
    const groupRows = isAssetGroup(group)
      ? flattenAssetsForNav([group])
      : flattenExchangeGroupsForNav([group])
    rows.push(...groupRows.map((row) => ({ ...row, assetIndex })))
  })
  return rows
}

// assetOnlySelection variant: one nav row per (asset, exchange) pair — its
// representative instrument — instead of one per leaf contract, matching the
// one-level-deep (ticker -> exchange) row rendering that mode uses.
export const flattenAssetGroupsForNavCollapsed = (groups: WizardAsset[]): SearchNavRow[] => {
  const rows: SearchNavRow[] = []
  groups.forEach((asset, assetIndex) => {
    for (const exchangeGroup of groupByExchange(asset.instruments)) {
      const representative = representativeInstrument(exchangeGroup.instruments)
      if (representative) rows.push({ instrument: representative, assetIndex })
    }
  })
  return rows
}

export const representativeRank = (instrument: Instrument): number => {
  const isPerpetual =
    instrument.kind === InstrumentKind.INSTRUMENT_KIND_FUTURE && isPerpInstrument(instrument)
  if (instrument.exchange === Exchange.EXCHANGE_DERIBIT && isPerpetual) return 0
  if (instrument.exchange === Exchange.EXCHANGE_HYPERLIQUID && isPerpetual) return 1
  if (instrument.exchange === Exchange.EXCHANGE_BYBIT && isPerpetual) return 2
  // No product signal yet for where Lighter should rank relative to the
  // established venues above — park it last among perps until there is one.
  if (instrument.exchange === Exchange.EXCHANGE_LIGHTER && isPerpetual) return 4
  if (isPerpetual) return 3
  if (instrument.kind === InstrumentKind.INSTRUMENT_KIND_SPOT) return 5
  if (instrument.kind === InstrumentKind.INSTRUMENT_KIND_FUTURE) return 6
  return 7
}

export const representativeInstrument = (instruments: Instrument[]): Instrument | undefined => {
  let representative: Instrument | undefined
  let bestRank = Number.POSITIVE_INFINITY
  for (const instrument of instruments) {
    const rank = representativeRank(instrument)
    if (rank < bestRank) {
      representative = instrument
      bestRank = rank
    }
  }
  return representative
}

// The set an asset's "select all" controls should actually add — every
// non-option instrument as-is, plus one representative per option venue+quote
// chain rather than every leaf strike/expiry. Keeps bulk-select consistent
// with the collapsed rows the multi-select detail pane renders (see
// representativeOptionInstrument above) instead of silently adding hundreds
// of option contracts a user never saw.
export const selectableAssetInstruments = (
  instruments: Instrument[],
  underlyingPrice: number | undefined
): Instrument[] => {
  const nonOptions = instruments.filter(
    (instrument) => instrument.kind !== InstrumentKind.INSTRUMENT_KIND_OPTION
  )
  const options = instruments.filter(
    (instrument) => instrument.kind === InstrumentKind.INSTRUMENT_KIND_OPTION
  )
  if (options.length === 0) return nonOptions
  const representatives = groupOptionsByVenueQuote(options).flatMap((venueGroup) => {
    const representative = representativeOptionInstrument(venueGroup.instruments, underlyingPrice)
    return representative ? [representative] : []
  })
  return [...nonOptions, ...representatives]
}

// representativeInstrument alone picks arbitrarily among options — every option
// shares the same rank (7), so ties just keep whichever came first in the
// catalog's own order, landing a single-select picker (e.g. "+" a new chart
// tab) on a random, possibly deep-OTM/far-expiry contract. Prefers a real
// non-option instrument (perp > spot > future) exactly like
// representativeInstrument; only when an asset trades EXCLUSIVELY as options
// does it fall through to a deliberately chosen contract instead — the
// asset's most-preferred venue/quote chain's nearest expiry, nearest-the-money
// strike (no live underlying price is available at this call site, so ties
// fall back to the median strike — see nearestStrikeInstrument).
export const representativeInstrumentForAsset = (
  instruments: Instrument[]
): Instrument | undefined => {
  const nonOptions = instruments.filter(
    (instrument) => instrument.kind !== InstrumentKind.INSTRUMENT_KIND_OPTION
  )
  if (nonOptions.length > 0) return representativeInstrument(nonOptions)
  const [preferredVenueGroup] = groupOptionsByVenueQuote(instruments)
  if (!preferredVenueGroup) return undefined
  return representativeOptionInstrument(preferredVenueGroup.instruments, undefined)
}

export const formatPrice = (value: string | undefined): string | undefined => {
  if (!value) return undefined
  const price = Number(value)
  if (!Number.isFinite(price)) return undefined
  return price.toLocaleString('en-US', { maximumFractionDigits: 6 })
}

// Selection state for a group's "select all" header control — instruments already
// on the watchlist aren't selectable, so they're excluded from the tri-state math.
export const groupSelectionState = (
  groupInstruments: Instrument[],
  existingKeys: Set<string>,
  selectedKeys: Set<string>
) => {
  const selectable = groupInstruments.filter(
    (instrument) => !existingKeys.has(watchlistEntryKey(instrument.exchange, instrument.symbol))
  )
  const allSelected =
    selectable.length > 0 &&
    selectable.every((instrument) =>
      selectedKeys.has(watchlistEntryKey(instrument.exchange, instrument.symbol))
    )
  const noneSelected = selectable.every(
    (instrument) => !selectedKeys.has(watchlistEntryKey(instrument.exchange, instrument.symbol))
  )
  return { selectable, allSelected, noneSelected }
}
